// Страницы документации раздела «Каталог» и «Вебхуки через API» (docs/integrations/CATALOG-API.md).
// Все методы — с заголовком uuid компании и Basic-авторизацией (раздел «Интеграции внешних систем»).

import type { ApiDocPage } from '../types'

export const catalogApiDocs: Record<string, ApiDocPage> = {
  import: {
    link: ['POST /api/integration/v1/import/catalog'],
    blocks: [
      {
        id: 'params',
        heading: 'Тело запроса',
        text: [
          'Тело — в формате фида каталога (страница «Фид каталога»): categories, tags, components, properties, property_enum_values, products, offers. Связи — по ext_id. Присылать можно пачками; повтор того же запроса безопасен — дублей не будет.',
          'Каждая позиция сохраняется отдельно: ошибка одной (например, предложение с несуществующим product_ext_id) не отменяет остальные и возвращается в errors с путём до позиции. Ошибка формата (нет обязательного поля, неверный тип) — 422 на весь запрос.',
          'Товар или предложение, удалённые раньше (в кабинете или методом удаления), с тем же ext_id восстанавливаются.',
          'Простой товар (simple: true, по умолчанию) продаётся одним предложением, цена указывается у товара. У товара с вариантами (simple: false) цена задаётся в предложениях раздела offers; цена, присланная у самого товара, не применяется. Когда приходит первое предложение товара, единственное предложение простого товара убирается.',
          'Картинки (JPEG, PNG, GIF, WebP) загружаются в фоне после ответа. Картинка с тем же адресом повторно не скачивается; адрес, пропавший из image_urls, удаляется. Не загрузившиеся адреса видны в выгрузке каталога (image_errors).',
          'Импорты одной компании выполняются по очереди: если идёт другой импорт, запрос ждёт до 90 секунд, затем отвечает 409.',
        ],
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: `{
  "success": 1,
  "data": {
    "message": "ok",
    "created": 3,
    "updated": 10,
    "restored": 1,
    "failed": 1,
    "errors": [
      {
        "section": "offers",
        "ext_id": "offer-15",
        "path": "offers.4",
        "message": "Товар с ext_id 'crm-77' не найден — пришлите его в разделе products"
      }
    ],
    "sections": {
      "products": { "created": 1, "updated": 6, "restored": 1, "failed": 0 },
      "offers": { "created": 2, "updated": 4, "restored": 0, "failed": 1 }
    },
    "images_queued": 7
  },
  "error": null
}`,
      },
      {
        id: 'errors',
        heading: 'Ошибки',
        text: [
          '422 — ошибка формата, ничего не сохранено: в error.message — первая ошибка, в error.errors — все по полям (например, products.3.title).',
          '409 — идёт другой импорт или удаление этой компании: error.message с объяснением, запрос можно повторить.',
          '401 — неверные логин и пароль Basic или uuid компании; 504 — см. ниже. У этих двух error — строка, а не объект.',
        ],
        code: `{
  "success": 0,
  "data": null,
  "error": {
    "message": "Поле products.3.title обязательно для заполнения.",
    "errors": { "products.3.title": ["Поле products.3.title обязательно для заполнения."] }
  }
}`,
      },
      {
        id: 'timeout',
        heading: 'Если ответ не пришёл',
        text: [
          'Ответ 504 «Каталог не ответил за 110 секунд» значит, что запрос мог примениться. Повторите его — повтор безопасен — или сверьте каталог методом выгрузки. Чтобы не упираться во время, присылайте пачки по 50–100 товаров.',
        ],
      },
    ],
  },
  delete: {
    link: ['POST /api/integration/v1/import/catalog/delete'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Нужен хотя бы один список, в каждом — до 1000 ext_id.',
          'Товар удаляется вместе со всеми предложениями, включая выключенные. Если у товара удалены все предложения, удаляется и сам товар — он попадает в deleted_products. Категория удаляется насовсем, её подкатегории переходят к её родителю.',
          'Удалённый товар или предложение вернётся, если снова прислать его ext_id в импорте. Повтор запроса удаления безопасен: уже удалённые товары и предложения снова попадут в deleted и deleted_offers. Категория удаляется насовсем, поэтому при повторе она окажется в not_found_categories.',
        ],
        code: `{
  "ext_ids": ["crm-test-2"],          // товары
  "offer_ext_ids": ["crm-offer-15"],  // торговые предложения
  "category_ext_ids": ["cat-old"]     // категории
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: `{
  "success": 1,
  "data": {
    "deleted": ["crm-test-2"],
    "not_found": [],
    "deleted_offers": ["crm-offer-15"],
    "not_found_offers": [],
    "deleted_products": [],
    "deleted_categories": ["cat-old"],
    "not_found_categories": []
  },
  "error": null
}`,
      },
    ],
  },
  export: {
    link: ['GET /api/integration/v1/catalog/products', 'GET /api/integration/v1/catalog/categories'],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры выгрузки товаров',
        text: [
          'Товары отдаются в том же формате, что принимает импорт: те же поля, включая состав (components), значения свойств (enum_property_values, string_property_values) и похожие товары (similar), связи по ext_id, адреса картинок — те, что вы присылали. Запись можно сравнить со своей поле в поле. Дополнительно приходят id, deleted, deleted_at, updated_at и image_errors.',
          'У простого товара цена, старая цена, скидка, бонус, штрихкод, состав и свойства — у самого товара, как в импорте; offers у него пустой. Выключенные предложения тоже выгружаются (active: false).',
          'Страницы листаются курсором: возьмите next_cursor из ответа и передайте в cursor следующего запроса, пока has_more не станет false. Курсор, в отличие от номера страницы, не сдвигается, если каталог меняется во время выгрузки. total приходит на первой странице.',
          'Для регулярной сверки передавайте updated_since — время прошлой сверки: придут только товары, изменённые после него, включая изменения предложений. С with_deleted=1 придут и удалённые (deleted: true).',
          'Категории — одним списком: GET /api/integration/v1/catalog/categories. Поля — как в импорте (ext_id, name, parent_ext_id, active, image_url), плюс id, image_errors и updated_at.',
        ],
        code: `limit=500                              // до 1000, по умолчанию 500
cursor=eyJpZCI6NTAwfQ                  // из next_cursor предыдущего ответа
updated_since=2026-10-07T12:00:00+03:00
with_deleted=1`,
      },
      {
        id: 'success',
        heading: 'Пример ответа',
        code: `{
  "success": 1,
  "data": {
    "data": [
      {
        "id": 1042,
        "ext_id": "crm-test-2",
        "title": "Букет «Рассвет»",
        "description": "15 роз",
        "simple": false,
        "price": null,
        "active": true,
        "categories": ["cat-roses"],
        "tags": [],
        "similar": ["crm-test-7"],
        "components": [],
        "enum_property_values": [],
        "string_property_values": [],
        "main_image_url": "https://crm.example.com/img/1.jpg",
        "image_urls": ["https://crm.example.com/img/2.jpg"],
        "image_errors": [],
        "deleted": false,
        "deleted_at": null,
        "updated_at": "2026-10-07T12:30:00+03:00",
        "offers": [
          {
            "id": 1043,
            "ext_id": "crm-offer-15",
            "product_ext_id": "crm-test-2",
            "title": "15 роз",
            "price": 3500,
            "old_price": 4000,
            "discount": 12,
            "active": true,
            "components": [{ "ext_id": "rose-red", "quantity": 15, "unit": "piece" }],
            "enum_property_values": ["color-red"],
            "string_property_values": [{ "property_ext_id": "card", "value": "С днём рождения" }],
            "main_image_url": null,
            "image_urls": [],
            "image_errors": [],
            "deleted": false,
            "deleted_at": null,
            "updated_at": "2026-10-07T12:30:00+03:00"
          }
        ]
      }
    ],
    "next_cursor": "eyJpZCI6MTA0Mn0",
    "has_more": true,
    "total": 2400
  },
  "error": null
}`,
      },
    ],
  },
  sync: {
    link: [
      'POST /api/integration/v1/import/catalog/sync',
      'GET /api/integration/v1/import/catalog/sync/:id',
      'POST /api/integration/v1/import/catalog/sync/:id/finish',
    ],
    blocks: [
      {
        id: 'params',
        heading: 'Как проходит полная синхронизация',
        text: [
          '1. Начните сеанс: POST …/sync — в ответе session_id. Новый сеанс отменяет незавершённый.',
          '2. Пришлите весь каталог обычным импортом, сколькими угодно пачками.',
          '3. Завершите: POST …/sync/:id/finish. Удаляются товары, предложения и категории с ext_id, которые с начала сеанса не приходили. Позиции без ext_id (заведены в кабинете или пришли из 1С) не трогаются.',
          'Сначала можно посмотреть, что будет удалено: finish с dry_run: true ничего не удаляет, сеанс остаётся открытым. В ответе to_delete — по товарам, предложениям и категориям число (count) и до 1000 ext_id (ext_ids).',
          'Предохранитель: если с начала сеанса пришло меньше половины товаров каталога, завершение отвечает 409 — похоже, выгрузка оборвалась. Если удалить остальные действительно нужно, передайте force: true.',
          'Состояние сеанса — GET …/sync/:id: сколько товаров пришло (products_seen) из скольких (products_total); после завершения — ещё сколько удалено (products_deleted, offers_deleted, categories_deleted).',
          'Ошибки завершения: 409 — сеанс уже завершён, отменён новым сеансом или сработал предохранитель; 404 — сеанс не найден.',
        ],
        code: `{
  "dry_run": true,   // только показать, что будет удалено
  "force": false     // удалить, даже если пришло меньше половины
}`,
      },
      {
        id: 'dry-run',
        heading: 'Пример ответа на пробное завершение (dry_run: true)',
        code: `{
  "success": 1,
  "data": {
    "session_id": "9b5e0f4a-6d5e-4f0c-9c8b-1f2a3d4e5f6a",
    "status": "open",
    "started_at": "2026-10-07T10:00:00+03:00",
    "finished_at": null,
    "products_total": 2400,
    "products_seen": 2390,
    "dry_run": true,
    "to_delete": {
      "products": { "count": 10, "ext_ids": ["crm-old-1"] },
      "offers": { "count": 4, "ext_ids": ["crm-offer-old"] },
      "categories": { "count": 1, "ext_ids": ["cat-old"] }
    }
  },
  "error": null
}`,
      },
      {
        id: 'success',
        heading: 'Пример ответа на завершение',
        code: `{
  "success": 1,
  "data": {
    "session_id": "9b5e0f4a-6d5e-4f0c-9c8b-1f2a3d4e5f6a",
    "status": "finished",
    "started_at": "2026-10-07T10:00:00+03:00",
    "finished_at": "2026-10-07T10:42:00+03:00",
    "products_total": 2400,
    "products_seen": 2390,
    "products_deleted": 10,
    "offers_deleted": 4,
    "categories_deleted": 1,
    "dry_run": false,
    "deleted": ["crm-old-1"],
    "not_found": [],
    "deleted_offers": ["crm-offer-old"],
    "not_found_offers": [],
    "deleted_products": [],
    "deleted_categories": ["cat-old"],
    "not_found_categories": []
  },
  "error": null
}`,
      },
    ],
  },
  webhooks: {
    link: [
      'GET /api/integration/v1/webhooks',
      'GET /api/integration/v1/webhooks/events',
      'POST /api/integration/v1/webhooks',
      'GET /api/integration/v1/webhooks/:id',
      'PATCH /api/integration/v1/webhooks/:id',
      'DELETE /api/integration/v1/webhooks/:id',
    ],
    blocks: [
      {
        id: 'params',
        heading: 'Параметры',
        text: [
          'Те же вебхуки, что в кабинете (раздел «Вебхуки»), с теми же правилами. Список событий — GET …/webhooks/events. При изменении (PATCH) передаются только меняющиеся поля. Пароль Basic вебхука в ответах не показывается.',
        ],
        code: `{
  "event": "order.created",
  "method": "POST",
  "url": "https://crm.example.com/ifb/hook",
  "basic_auth_login": "hook",      // не обязательно
  "basic_auth_password": "secret", // обязательно вместе с логином
  "headers": { "X-Source": "ifb" },// не обязательно
  "is_active": true                // не обязательно
}`,
      },
    ],
  },
}
